import { useTranslation } from 'react-i18next';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { OutputCellModalTable } from './OutputCellModalTable';
import { OutputCellModalTitle } from './OutputCellModalTitle';
import {
  useDisconnectProcessBlock,
  useGetOutputCellConnections,
} from 'api/services/processMapService';
import { useUI } from 'contexts/UiContext';
import { ErrorType } from 'types/genericTypes';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useState } from 'react';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';

type OutputCellModalProps = {
  open: boolean;
  onCancel: () => void;
  outputCellId: string | number;
  planId: string;
};

export const OutputCellBlockTranslations = ['end-of-flow', 'unknown-target'];

export const OutputCellModal = ({
  open,
  onCancel,
  outputCellId,
  planId,
}: OutputCellModalProps) => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();
  const [disconnectModal, setDisconnectModal] = useState<string>('');
  const [disconnectionProcessName, setDisconnectionProcessName] =
    useState<string>('');

  const { data: connectionsData, isLoading: isConnectionsLoading } =
    useGetOutputCellConnections({
      companyId: selectedCompany || '',
      planId,
      outputCellId,
    });

  const {
    mutateAsync: disconnectProcess,
    isPending: isDisconnectProcessPending,
  } = useDisconnectProcessBlock();

  const connections = connectionsData?.data?.connections ?? [];

  const disconnectButtonHandler = async (connectionId: string) => {
    try {
      await disconnectProcess({
        companyId: selectedCompany || '',
        planId,
        outputCellId,
        connectionId,
      });

      openNotification(
        t('success'),
        t('outputcell.disconnect-successfully'),
        'success'
      );
      setDisconnectModal('');
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('outputcell.disconnect-non-successfully'),
          'error'
        );
      }
    }
  };

  const getTranslatedName = (name: string) => {
    if (OutputCellBlockTranslations.includes(name)) {
      return t(`process-map.${name}`);
    }
    // Fallback: return the raw name if not a known translation key
    return name;
  };

  return (
    <DarwinModal
      style={{ minHeight: 400 }}
      width={'85%'}
      open={open}
      onCancel={onCancel}
      centered
      footer={null}
      title={<OutputCellModalTitle title={'Output Cell'} />}
    >
      <GenericDeleteModal
        open={!!disconnectModal}
        onCancel={() => setDisconnectModal('')}
        onOk={() => disconnectButtonHandler(disconnectModal)}
        okTitle={t('outputcell.ok-text')}
        confirmLoading={isDisconnectProcessPending}
        bodyText={`${t('outputcell.disconnect-confirmation-primary-text')}
        ${`"${disconnectionProcessName}" ?`} 
        ${t('outputcell.disconnect-confirmation-second-text')}`}
        okType="default"
        okButtonProps={{ danger: true, style: { fontWeight: 600 } }}
      />

      {isConnectionsLoading && (
        <DarwinFlex
          align="center"
          justify="center"
          style={{
            height: 200,
          }}
        >
          <DarwinSpin />
        </DarwinFlex>
      )}

      {!isConnectionsLoading &&
        connections.map(connection => (
          <OutputCellModalTable
            key={connection.connectionId}
            planId={planId}
            outputCellId={outputCellId}
            connectionId={connection.connectionId}
            sourceBlockName={getTranslatedName(connection.sourceBlockName)}
            targetBlockName={getTranslatedName(connection.targetBlockName)}
            canDisconnect={connection.canDisconnect}
            onDisconnect={(connectionId, processName) => {
              setDisconnectModal(connectionId);
              setDisconnectionProcessName(processName);
            }}
          />
        ))}
    </DarwinModal>
  );
};
